import * as React from 'react'

import * as api from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'

import * as projectModel from '../../../models/project'

export function useShippingInfo(project: projectModel.Project) {
  const rewardsHaveCustomDeliveries = project.rewards?.some((reward) => reward.has_custom_delivery) ?? false

  if (!project.shippings.zones) {
    return {
      rewardsHaveCustomDeliveries,
      selectedCountry: undefined,
      setSelectedCountry: () => {},
      shippingZone: undefined,
      supportedCountries: [],
      supportedCountryCodes: undefined,
    }
  }

  // We use Set so that we don't have duplicated countries
  const supportedCountryCodes = project.shippings.zones.reduce(
    (countryArray: string[], zone: api.ShippingZone) => [...new Set([...countryArray, ...(zone.all_countries ?? [])])],
    [],
  )

  const userCountry = env.USER_COUNTRY

  const [selectedCountry, setSelectedCountry] = React.useState(
    userCountry && supportedCountryCodes.includes(userCountry) ? userCountry : project.country,
  )

  const supportedCountries = supportedCountryCodes
    .map((code) => ({
      label: getCountryLabel(code),
      code,
    }))
    .filter((option): option is { label: string; code: string } => option.label !== undefined)
    .sort((a, b) => a.label.localeCompare(b.label))

  const shippingZone =
    supportedCountries.length === 1
      ? project.shippings.zones[0]
      : findShippingZoneForCountry(
          project.shippings.zones.filter((zone) => zone.all_countries?.includes(selectedCountry)),
        )

  return {
    supportedCountries,
    supportedCountryCodes,
    selectedCountry,
    setSelectedCountry,
    shippingZone,
    rewardsHaveCustomDeliveries,
  }
}

export function getCountryLabel(countryCode: string) {
  const country = env.COUNTRIES.find((country) => country.code === countryCode)

  if (country === undefined) {
    return undefined
  }

  return country.label()
}

function findShippingZoneForCountry(zones: api.ShippingZone[]): api.ShippingZone | undefined {
  if (zones.length === 1) {
    return zones[0]
  }

  // Priority rules for shippings:
  // - zones with searched country but no area has top priority
  // - then european-union > europe > rest-of-the-world and overseas-fr > rest-of-the-world
  const singleZone = zones.find((zone) => zone.areas === null)
  if (singleZone) return singleZone

  const europeArea = zones.find((zone) => zone.areas?.includes('european-union'))
  if (europeArea) return europeArea

  const overeasFrArea = zones.find((zone) => zone.areas?.includes('overseas-fr'))
  if (overeasFrArea) return overeasFrArea

  const europeUnionZone = zones.find((zone) => zone.areas?.includes('european'))
  if (europeUnionZone) return europeUnionZone

  const restOfTheWorldZone = zones.find((zone) => zone.areas?.includes('rest-of-world'))
  if (restOfTheWorldZone) return restOfTheWorldZone
}
